import {
  computed, inject, toRaw,
} from 'vue';
import useDayjs from './useDayjs';
import useFactoryContext from './useFactoryContext';
import { isAvailabilityPeriodsEnabled } from './useTenant';
import { hmsToSeconds } from '../helpers/date';

/**
 * Composable for schedules, shifts and calendars
 */
export default function useSchedule() {
  const { $dayjs, convertWeekDayIntoDay } = useDayjs();

  const useAvailabilityPeriods = computed(() => isAvailabilityPeriodsEnabled());

  /**
   * The absolute duration of the shift in seconds
   */
  function getScheduleShiftDuration(shift) {
    // "endTime" can overflow on the next day, we need to check this
    const normalizeTime = (time) => {
      const firstColon = time.indexOf(':');
      if (firstColon === -1) return time;
      const secondColon = time.indexOf(':', firstColon + 1);
      if (secondColon === -1) return `${time}:00:000`;

      return time;
    };

    const startTimeStr = normalizeTime(shift.start);
    const endTimeStr = normalizeTime(shift.end);
    const startInSeconds = hmsToSeconds(startTimeStr);
    let endInSeconds = hmsToSeconds(endTimeStr);
    if (endInSeconds <= startInSeconds) {
      // It should mean that the end time occurs on the next day
      endInSeconds += 3600 * 24;
    }

    return Math.abs(endInSeconds - startInSeconds);
  }

  /**
   * Parent site (can be null)
   */
  const parentSite = inject(
    'parentSite',
    // Default site
    {
      firstDayOfWeek: 'MONDAY',
      startProductionTime: '00:00:00',
      timezone: 'UTC',
    },
  );

  // Same provider as useFactoryContext, read through it so the injection has a single owner.
  // The raw machines are kept: consumers here read their `equipmentId`, not their `id`.
  const { siteMachines: selectedSiteMachines } = useFactoryContext();

  const dayIndexByKey = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
  };
  /**
   * The first day of week can actually have an offset
   */
  const startProductionDayOffset = computed(() => parentSite?.value?.startProductionDayOffset ?? 0);
  /**
   * The index of the first day the week
   */
  const firstDayOfWeekIndex = computed(() => {
    const firstDay = parentSite?.value?.firstDayOfWeek ?? 'MONDAY';
    const index = dayIndexByKey[firstDay] ?? 1;

    return index - startProductionDayOffset.value;
  });

  /** Localized weekday name for vue-i18n locale */
  function dayLabel(dayIndex) {
    return convertWeekDayIntoDay(dayIndex, 'dddd');
  }

  /**
   * The days of the week before (not ordered)
   */
  const weekDaysRaw = computed(() => ([
    { index: 1, key: 'MONDAY', label: dayLabel(1) },
    { index: 2, key: 'TUESDAY', label: dayLabel(2) },
    { index: 3, key: 'WEDNESDAY', label: dayLabel(3) },
    { index: 4, key: 'THURSDAY', label: dayLabel(4) },
    { index: 5, key: 'FRIDAY', label: dayLabel(5) },
    { index: 6, key: 'SATURDAY', label: dayLabel(6) },
    { index: 0, key: 'SUNDAY', label: dayLabel(0) },
  ]));
  /**
   * The first day of week
   */
  const firstDayOfWeek = computed(() => parentSite?.value?.firstDayOfWeek ?? 'MONDAY');
  /**
   * The time the production starts
   */
  const startProductionTime = computed(() => parentSite?.value?.startProductionTime ?? '00:00:00');
  /**
   * The timezone for schedules
   */
  const timezone = computed(() => parentSite?.value?.timezone ?? 'UTC');
  /**
   * A default week based on today and all the parent settings
   * (timezone, startProductionTime, firstDayOfWeek...).
   * It returns the"start" and the "end" of the week.
   */
  const defaultWeek = computed(() => {
    const tz = toRaw(timezone.value) || 'UTC';
    // "base" will be the date we will base our calculation of "start" and "end"
    let base = $dayjs().tz(tz);

    // If I'm not careful, I can have next week, so I need to do this little check
    if (base.day(firstDayOfWeekIndex.value).isAfter(base)) {
      base = base.subtract(1, 'week');
    }
    base = base.day(firstDayOfWeekIndex.value);

    const start = $dayjs.tz(`${base.format('YYYY-MM-DD')} ${startProductionTime.value}`, tz);
    const end = start.add(7, 'day');

    return { start, end };
  });

  // TODO : TO DISCUSS ABOUT startProductionTime (instead of defaultWeek )
  const previewWeek = computed(() => {
    const tz = toRaw(timezone.value) || 'UTC';

    let base = $dayjs().tz(tz);

    if (base.day(firstDayOfWeekIndex.value).isAfter(base)) {
      base = base.subtract(1, 'week');
    }

    base = base.day(firstDayOfWeekIndex.value).startOf('day');

    const start = base;
    const end = start.add(7, 'day').startOf('day');

    return { start, end };
  });

  /**
   * Returns a dayjs object converted to the parent site's timezone.
   * If no date is provided, returns the current time in the site timezone.
   *
   * @param {string|Date|import('dayjs').Dayjs|null} [date]
   * @param {boolean} [sourceIsUtc=true]
   * @returns {import('dayjs').Dayjs}
   */
  function toSiteDate(date = null, sourceIsUtc = true) {
    const tz = toRaw(timezone.value) || 'UTC';

    if (!date) {
      return $dayjs().tz(tz);
    }

    if (sourceIsUtc) {
      return $dayjs.utc(date).tz(tz);
    }

    return $dayjs(date).tz(tz);
  }

  /**
   * Split the period between start and end by day if step=1, by 48 hours if step=2...
   */
  function splitByDay(start, end, step = 1) {
    const startDate = $dayjs(start);
    const endDate = $dayjs(end);
    if (!startDate.isValid() || !endDate.isValid()) return [];

    const stepDays = step > 0 ? step : 1;
    const startMs = startDate.valueOf();
    const endMs = endDate.valueOf();
    if (endMs <= startMs) return [];

    const total = Math.ceil(endDate.diff(startDate, 'day', true) / stepDays);
    const periods = new Array(total);
    for (let i = 0; i < total; i += 1) {
      const periodStart = startDate.add(i * stepDays, 'day');
      let periodEnd = periodStart.add(stepDays, 'day');
      if (periodEnd.valueOf() > endMs) {
        periodEnd = endDate;
      }
      periods[i] = { start: periodStart, end: periodEnd };
    }

    return periods;
  }

  return {
    defaultWeek,
    previewWeek,
    firstDayOfWeek,
    firstDayOfWeekIndex,
    getScheduleShiftDuration,
    parentSite,
    selectedSiteMachines,
    splitByDay,
    startProductionTime,
    toSiteDate,
    startProductionDayOffset,
    timezone,
    weekDaysRaw,
    useAvailabilityPeriods,
  };
}
